import {t, useLanguage, interfaceLocale} from '../../i18n';
// src/adminCalendar/components/CalendarToolbar.jsx
// LOOP R0.3.2 — Unified Calendar Toolbar
// Shared agenda toolbar used as the single web/header surface for date navigation,
// view switching, worker filtering and the right rail toggle.

import React from "react";
import {weekRangeLabel} from '../../utils/agendaRangeUi';
import MiniMonth from "../../components/MiniMonth";
import { startOfDay, formatSelectedDateHuman as defaultFormatSelectedDateHuman } from "../../utils/dateUtils";

function getAgendaWeekStartLocal(baseDate, weekStartsOn = "monday") {
  const d = startOfDay(baseDate || new Date());
  const targetDow = weekStartsOn === "sunday" ? 0 : 1;
  const currentDow = d.getDay();
  const diff = (currentDow - targetDow + 7) % 7;
  return startOfDay(new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff));
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getBestDayWhenLeavingRangeViewLocal(baseDate) { return startOfDay(baseDate || new Date()); }


function normalizeToolbarRoleContext(roleContext = null) {
  const ctx = roleContext && typeof roleContext === "object" ? roleContext : {};
  const rawRole = String(ctx.role || (ctx.isWorkerView ? "worker" : ctx.isOwnerOrAdmin ? "owner" : "staff")).toLowerCase();
  const role = ["owner", "admin", "worker", "staff"].includes(rawRole) ? rawRole : "staff";
  const isWorker = role === "worker" || ctx.isWorkerView === true;
  const isOwnerOrAdmin = role === "owner" || role === "admin" || ctx.isOwnerOrAdmin === true;
  return {
    role,
    isWorker,
    isOwnerOrAdmin,
    label: ctx.label || (isOwnerOrAdmin ? "Owner/Admin" : isWorker ? "Trabajador" : "Equipo"),
    sublabel: ctx.sublabel || (isOwnerOrAdmin ? "Vista completa" : isWorker ? "Mi agenda" : "Vista operativa"),
  };
}

function workerInitialsFallback(name = "") {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "?";
}

export default function CalendarToolbar({
  compact = false,
  dropdownRef = null,
  viewMode = "day",
  setViewMode,
  selectedDate = new Date(),
  setSelectedDate,
  currentMonthDate = new Date(),
  setCurrentMonthDate,
  agendaSettings = {},
  viewDropdownOpen = false,
  setViewDropdownOpen,
  workerFilterOpen = false,
  setWorkerFilterOpen,
  canCreateBooking = false,
  openSmartCreateForDate,
  canShowAgendaRightRail = false,
  showAgendaRightRail = false,
  setRightRailCollapsed,
  canShowWorkerFilter = false,
  canShowAgendaMobileControls = false,
  visibleEmployeeCount = 0,
  employees = [],
  selectedSet = new Set(),
  setSelectedEmployeeIds,
  toggleEmployeeId,
  getAgendaWeekStart = getAgendaWeekStartLocal,
  getBestDayWhenLeavingRangeView = getBestDayWhenLeavingRangeViewLocal,
  formatSelectedDateHuman = defaultFormatSelectedDateHuman,
  workerInitials = workerInitialsFallback,
  roleContext = null,
}) {
  useLanguage();
  const role = normalizeToolbarRoleContext(roleContext);

  const safeSettings = {
    weekStartsOn: agendaSettings.weekStartsOn || "monday",
    showWeekends: agendaSettings.showWeekends !== false,
    ...agendaSettings,
  };

  // LOOP_DATE_POPOVER_PREPAINT_OWNER_V57
  // CalendarToolbar owns web-mobile date-popover geometry before paint.
  // It measures the rendered header/toolbar instead of trusting legacy CSS variables.
  const datePopoverRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (!viewDropdownOpen || typeof window === "undefined" || typeof document === "undefined") return undefined;

    const popover = datePopoverRef.current;
    if (!popover) return undefined;

    const toolbarEl = popover.closest(".agenda-web-toolbar");
    const rootEl = popover.closest(".admin-calendar-root.phase23-clean");
    const dateAnchorEl = popover.closest(".agenda-date-anchor");
    const isWebMobile = Boolean(
      toolbarEl &&
      rootEl &&
      rootEl.classList.contains("is-web-revamp") &&
      window.matchMedia("(max-width: 980px)").matches
    );

    if (!isWebMobile) return undefined;

    const setImportant = (el, prop, value) => {
      if (!el || !el.style) return;
      try { el.style.setProperty(prop, String(value), "important"); } catch (_) {}
    };

    const placePopover = () => {
      if (!popover.isConnected || !toolbarEl.isConnected) return;

      const toolbarRect = toolbarEl.getBoundingClientRect();
      const headerEl = document.querySelector("#app-main-header, .loop-ref-header, .loop-mobile-header-v2");
      const headerRect = headerEl?.getBoundingClientRect?.();
      const headerBottom = Number.isFinite(headerRect?.bottom) ? headerRect.bottom : 58;
      const toolbarHeight = toolbarRect.height > 0 ? toolbarRect.height : 50;

      // Two independent measurements make first paint resilient even if an older
      // toolbar rule has not finished normalizing its top position yet.
      const measuredBelowToolbar = toolbarRect.bottom + 8;
      const measuredRowsMinimum = headerBottom + toolbarHeight + 8;
      const topPx = Math.ceil(Math.max(measuredBelowToolbar, measuredRowsMinimum));
      const viewportHeight = Math.max(320, Number(window.innerHeight || document.documentElement.clientHeight || 0));
      const maxHeightPx = Math.max(180, Math.floor(viewportHeight - topPx - 12));

      Object.entries({
        position: "fixed",
        inset: "auto",
        top: `${topPx}px`,
        left: "12px",
        right: "12px",
        bottom: "auto",
        width: "auto",
        "max-width": "calc(100vw - 24px)",
        "max-height": `${maxHeightPx}px`,
        margin: "0px",
        transform: "none",
        translate: "none",
        display: "block",
        visibility: "visible",
        opacity: "1",
        overflow: "auto",
        "overflow-y": "auto",
        "overscroll-behavior": "contain",
        "box-sizing": "border-box",
        "z-index": "2147483638",
        "pointer-events": "auto",
      }).forEach(([prop, value]) => setImportant(popover, prop, value));

      // The hardlock keeps this anchor hidden while closed. Open state must be
      // visible before paint so the newly-mounted fixed popup cannot be clipped.
      setImportant(dateAnchorEl, "overflow", "visible");
      if (dateAnchorEl?.dataset) dateAnchorEl.dataset.loopDatePopoverAnchorOwner = "V57";
      setImportant(toolbarEl, "overflow", "visible");

      popover.dataset.loopDatePopoverOwner = "V57-calendar-toolbar-prepaint";
      popover.dataset.loopDatePopoverTop = String(topPx);
    };

    placePopover();

    const onViewportChange = () => placePopover();
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("orientationchange", onViewportChange);

    let resizeObserver = null;
    try {
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(placePopover);
        resizeObserver.observe(toolbarEl);
      }
    } catch (_) {}

    return () => {
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("orientationchange", onViewportChange);
      try { resizeObserver?.disconnect?.(); } catch (_) {}
      if (dateAnchorEl?.dataset?.loopDatePopoverAnchorOwner === "V57") {
        try { dateAnchorEl.style.setProperty("overflow", "hidden", "important"); } catch (_) {}
        delete dateAnchorEl.dataset.loopDatePopoverAnchorOwner;
      }
    };
  }, [viewDropdownOpen, canShowAgendaMobileControls]);

  const closeMenus = () => {
    if (setViewDropdownOpen) setViewDropdownOpen(false);
    if (setWorkerFilterOpen) setWorkerFilterOpen(false);
  };

  const goPrevious = () => {
    if (!setSelectedDate) return;
    if (viewMode === "month") {
      const next = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
      setSelectedDate(next);
      if (setCurrentMonthDate) setCurrentMonthDate((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
    } else if (viewMode === "weekCompany") {
      setSelectedDate(startOfDay(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() - 7)));
    } else {
      setSelectedDate(startOfDay(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() - 1)));
    }
    if (setViewDropdownOpen) setViewDropdownOpen(false);
  };

  const goToday = () => {
    const today = startOfDay(new Date());
    if (setSelectedDate) setSelectedDate(today);
    if (setCurrentMonthDate) setCurrentMonthDate(new Date(today.getFullYear(), today.getMonth(), 1));
    closeMenus();
  };

  const goNext = () => {
    if (!setSelectedDate) return;
    if (viewMode === "month") {
      const next = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
      setSelectedDate(next);
      if (setCurrentMonthDate) setCurrentMonthDate((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
    } else if (viewMode === "weekCompany") {
      setSelectedDate(startOfDay(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + 7)));
    } else {
      setSelectedDate(startOfDay(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + 1)));
    }
    if (setViewDropdownOpen) setViewDropdownOpen(false);
  };

  const selectView = (nextView) => {
    if (!setViewMode) return;
    if (nextView === "day") {
      const nextDate = getBestDayWhenLeavingRangeView(selectedDate, viewMode, safeSettings);
      setViewMode("day");
      if (setSelectedDate) setSelectedDate(nextDate);
      if (setCurrentMonthDate) setCurrentMonthDate(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
    } else if (nextView === "weekCompany") {
      setViewMode("weekCompany");
      // Switching view preserves the selected day.
    } else if (nextView === "month") {
      setViewMode("month");
      if (setCurrentMonthDate) setCurrentMonthDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
    closeMenus();
  };

  const selectAllWorkers = () => {
    if (typeof setSelectedEmployeeIds === "function") {
      setSelectedEmployeeIds((employees || []).map((e) => String(e.id)));
    }
  };

  const dateFull = viewMode === "month"
    ? selectedDate.toLocaleDateString(interfaceLocale(), { month: "long", year: "numeric" })
    : viewMode === "weekCompany"
      ? weekRangeLabel(selectedDate, safeSettings.weekStartsOn, safeSettings.showWeekends)
      : formatSelectedDateHuman(selectedDate);

  const dateCompact = viewMode === "month"
    ? selectedDate.toLocaleDateString(interfaceLocale(), { month: "short", year: "numeric" })
    : viewMode === "weekCompany"
      ? weekRangeLabel(selectedDate, safeSettings.weekStartsOn, safeSettings.showWeekends)
      : selectedDate.toLocaleDateString(interfaceLocale(), { day: "numeric", month: "short", year: "numeric" });

  const showFilters = canShowWorkerFilter || canShowAgendaMobileControls;

  return (
    <div className={`agenda-web-toolbar loop-calendar-toolbar role-${role.role}${role.isWorker ? " is-worker-toolbar" : ""}${role.isOwnerOrAdmin ? " is-owner-toolbar" : ""}${canShowAgendaMobileControls ? " is-mobile-toolbar" : ""}${workerFilterOpen || viewDropdownOpen ? " is-popover-open" : ""}`} ref={dropdownRef}>
      <div className="agenda-web-toolbar-left loop-calendar-toolbar-left">
        <button type="button" className="agenda-nav-btn" onClick={goPrevious} aria-label={t("Anterior")}>‹</button>
        <button type="button" className="agenda-today-btn" onClick={goToday}>{t("Hoy")}</button>
        <button type="button" className="agenda-nav-btn" onClick={goNext} aria-label={t("Siguiente")}>›</button>
        <span className={`agenda-role-pill role-${role.role}`} title={role.sublabel}>
          <strong>{role.label}</strong>
          <em>{role.sublabel}</em>
        </span>
      </div>

      <div className="agenda-date-anchor loop-calendar-toolbar-date">
        <button
          type="button"
          className="agenda-current-date"
          onClick={() => {
            if (setWorkerFilterOpen) setWorkerFilterOpen(false);
            if (setViewDropdownOpen) setViewDropdownOpen((s) => !s);
          }}
          aria-haspopup="true"
          aria-expanded={viewDropdownOpen}
        >
          <span className="agenda-date-text agenda-date-text-full">{dateFull}</span>
          <span className="agenda-date-text agenda-date-text-compact">{dateCompact}</span>
          <span className="agenda-date-chevron" aria-hidden="true">⌄</span>
        </button>

        {viewDropdownOpen ? (
          <div ref={datePopoverRef} className="agenda-date-popover">
            <MiniMonth
              currentMonthDate={currentMonthDate}
              selectedDate={selectedDate}
              onSelectDate={(d) => {
                if (setSelectedDate) setSelectedDate(d);
                if (setCurrentMonthDate) setCurrentMonthDate(new Date(d.getFullYear(), d.getMonth(), 1));
                if (setViewDropdownOpen) setViewDropdownOpen(false);
              }}
              onPrevMonth={() => setCurrentMonthDate && setCurrentMonthDate((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              onNextMonth={() => setCurrentMonthDate && setCurrentMonthDate((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            />
          </div>
        ) : null}
      </div>

      <div className="agenda-web-toolbar-right loop-calendar-toolbar-right">
        <div className="agenda-view-switch" role="group" aria-label={t("Vista de agenda")}>
          <button type="button" aria-pressed={viewMode === "day"} className={viewMode === "day" ? "is-active" : ""} onClick={() => selectView("day")}>{t("Día")}</button>
          <button type="button" aria-pressed={viewMode === "weekCompany"} className={viewMode === "weekCompany" ? "is-active" : ""} onClick={() => selectView("weekCompany")}>{t("Semana")}</button>
          <button type="button" aria-pressed={viewMode === "month"} className={viewMode === "month" ? "is-active" : ""} onClick={() => selectView("month")}>{t("Mes")}</button>
        </div>

        {canCreateBooking ? (
          <button
            type="button"
            className="agenda-new-booking-btn loop-single-create-plus-btn loop-single-create-plus-text-lock loop-native-plus-prepaint-v31"
            onClick={() => openSmartCreateForDate && openSmartCreateForDate(selectedDate)}
            aria-label={t("Crear reserva")}
            title={t("Crear reserva")}
          >
            +
          </button>
        ) : null}

        {canShowAgendaRightRail ? (
          <button
            type="button"
            className={`agenda-filter-btn agenda-panel-toggle-btn ${showAgendaRightRail ? "is-active" : "is-collapsed"}`}
            onClick={() => setRightRailCollapsed && setRightRailCollapsed((s) => !s)}
            aria-label={showAgendaRightRail ? "Ocultar panel lateral" : "Mostrar panel lateral"}
            aria-pressed={showAgendaRightRail}
            title={showAgendaRightRail ? "Ocultar panel lateral" : "Mostrar panel lateral"}
          >
            <span className="agenda-panel-icon" aria-hidden="true">{showAgendaRightRail ? "›" : "‹"}</span>
            <span className="agenda-panel-label">{showAgendaRightRail ? "Ocultar" : "Panel"}</span>
          </button>
        ) : null}

        {showFilters ? (
          <div className="agenda-worker-filter-wrap agenda-mobile-controls-wrap loop-calendar-toolbar-filter">
            <button
              type="button"
              className={`agenda-filter-btn agenda-worker-filter-btn ${workerFilterOpen ? "is-active" : ""}`}
              onClick={() => {
                if (setWorkerFilterOpen) setWorkerFilterOpen((s) => !s);
                if (setViewDropdownOpen) setViewDropdownOpen(false);
              }}
              aria-label={canShowAgendaMobileControls ? "Vista y filtros" : "Filtrar trabajadores"}
              aria-haspopup="true"
              aria-expanded={workerFilterOpen}
            >
              <span className="agenda-filter-icon" aria-hidden="true">☰</span>
              <span className="agenda-filter-label">{canShowAgendaMobileControls ? "Vista" : "Filtro"}</span>
              {canShowWorkerFilter ? <span className="agenda-filter-count">{visibleEmployeeCount}</span> : null}
            </button>

            {workerFilterOpen ? (
              <div className="agenda-worker-filter-popover" role="dialog" aria-label={canShowAgendaMobileControls ? "Vista y filtros" : "Trabajadores visibles"}>
                {canShowAgendaMobileControls ? (
                  <div className="agenda-mobile-view-filter">
                    <strong>{t("Vista")}</strong>
                    <div className="agenda-mobile-view-options" role="group" aria-label={t("Vista de agenda")}>
                      <button type="button" aria-pressed={viewMode === "day"} className={viewMode === "day" ? "is-active" : ""} onClick={() => selectView("day")}>{t("Día")}</button>
                      <button type="button" aria-pressed={viewMode === "weekCompany"} className={viewMode === "weekCompany" ? "is-active" : ""} onClick={() => selectView("weekCompany")}>{t("Semana")}</button>
                      <button type="button" aria-pressed={viewMode === "month"} className={viewMode === "month" ? "is-active" : ""} onClick={() => selectView("month")}>{t("Mes")}</button>
                    </div>
                  </div>
                ) : null}

                {canShowWorkerFilter ? (
                  <>
                    <div className="agenda-worker-filter-head">
                      <div>
                        <strong>{t("Equipo visible")}</strong>
                        <span>{visibleEmployeeCount} de {(employees || []).length}{" " + t("trabajadores")}</span>
                      </div>
                      <button
                        type="button"
                        onClick={selectAllWorkers}
                      >{t("Todos")}</button>
                    </div>

                    <div className="agenda-worker-filter-list">
                      {(employees || []).map((w) => {
                        const id = String(w.id);
                        const name = w.name || w.displayName || "Trabajador";
                        const photo = w.photoUrl || w.photo || w.image || w.avatarUrl || null;
                        const checked = selectedSet.size === 0 || selectedSet.has(id);
                        return (
                          <button
                            key={id}
                            type="button"
                            className={`agenda-worker-filter-row ${checked ? "is-selected" : ""}`}
                            onClick={() => toggleEmployeeId && toggleEmployeeId(id)}
                            aria-pressed={checked}
                          >
                            <span className="agenda-worker-filter-avatar">
                              {photo ? <img src={photo} alt={name} /> : workerInitials(name)}
                            </span>
                            <span className="agenda-worker-filter-copy">
                              <strong>{name}</strong>
                              <em>{checked ? "Visible" : "Oculto"}</em>
                            </span>
                            <span className="agenda-worker-filter-check">{checked ? "✓" : ""}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
